import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import api from '../../services/api';
import { useAuth } from '../auth/AuthContext';
import { buildLicenseSummary } from '../../utils/licenseUtils';
import { useI18n } from '../../i18n/I18nContext';

// Format price with currency
const formatPrice = (price, currency = 'USD') => {
  if (price === null || price === undefined) return 'Price not available';
  if (price === 0) return 'Free';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency
  }).format(price);
};

const BuyButtonContainer = styled.div`
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.75rem;
  width: 100%;
  & > * {
    flex: 1 1 0;
    min-width: 0;
  }
`;

const BuyButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-size: ${props => props.$inline ? '1rem' : '1.125rem'};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
  position: relative;
  overflow: hidden;
  min-width: ${props => props.$inline ? '0' : '200px'};
  flex: ${props => props.$inline ? '1 1 0' : 'none'};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.6);
    background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }

  &:hover:not(:disabled)::before {
    left: 100%;
  }
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 0.75rem;
  font-size: 0.875rem;
  text-align: center;
`;

const PriceDisplay = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #059669;
  margin-bottom: 0.5rem;
  text-align: center;
`;

const TipInput = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const TipButton = styled.button`
  padding: 0.35rem 0.75rem;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${props => props.$active ? '#10b981' : '#e5e7eb'};
  background: ${props => props.$active ? 'rgba(16,185,129,0.15)' : 'transparent'};
  color: ${props => props.$active ? '#10b981' : '#6b7280'};
  transition: all 0.2s;
  &:hover {
    border-color: #10b981;
  }
`;

const TipCustomInput = styled.input`
  width: 100%;
  max-width: 120px;
  padding: 0.5rem 0.75rem;
  border: 2px solid #10b981;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  text-align: center;
  background: rgba(16,185,129,0.05);
  color: inherit;
  margin-top: 0.35rem;
  &:focus {
    outline: none;
    border-color: #059669;
    box-shadow: 0 0 0 3px rgba(16,185,129,0.2);
  }
`;

const PwywInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #7c5cff;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  text-align: center;
  background: rgba(124,92,255,0.05);
  color: inherit;
  margin-bottom: 0.5rem;
  &:focus {
    outline: none;
    border-color: #5a3dc8;
    box-shadow: 0 0 0 3px rgba(124,92,255,0.2);
  }
`;

const CheckoutButtons = ({ items, onCheckout, onRequireLogin, addToCartSlot, license }) => {
  const { user } = useAuth();
  const { t } = useI18n();
  const [licenseAck, setLicenseAck] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tipPreset, setTipPreset] = useState(0); // 0 | 1 | 2 | 5 | 'other'
  const [tipCustomInput, setTipCustomInput] = useState('');
  const [pwywPrices, setPwywPrices] = useState({});
  const [displayItems, setDisplayItems] = useState(items);

  const effectiveTipAmount = tipPreset === 'other'
    ? Math.round((parseFloat(tipCustomInput) || 0) * 100) / 100
    : (typeof tipPreset === 'number' ? tipPreset : 0);
  const tipAmount = Math.max(0, effectiveTipAmount);

  // Hydrate items with cart-meta (tipEnabled, pricingType, minPrice) when any field is missing (e.g. legacy localStorage cart)
  useEffect(() => {
    if (!items || items.length === 0) {
      setDisplayItems([]);
      return;
    }
    const needsHydration = items.some(
      item => item.tipEnabled === undefined || item.pricingType === undefined || item.minPrice === undefined
    );
    if (!needsHydration) {
      setDisplayItems(items);
      return;
    }
    const ids = items.map(i => (i.productId && i.productId.toString) ? i.productId.toString() : i.productId).filter(Boolean);
    if (ids.length === 0) {
      setDisplayItems(items);
      return;
    }
    api.get('/products/cart-meta', { params: { ids: ids.join(',') } })
      .then(({ data: meta }) => {
        const metaByKey = (meta || []).reduce((acc, m) => {
          acc[m.productId] = m;
          return acc;
        }, {});
        const merged = items.map(item => {
          const key = (item.productId && item.productId.toString) ? item.productId.toString() : item.productId;
          const m = metaByKey[key];
          if (!m) return { ...item, tipEnabled: item.tipEnabled ?? false, pricingType: item.pricingType ?? 'fixed', minPrice: item.minPrice ?? 0 };
          return {
            ...item,
            tipEnabled: item.tipEnabled !== undefined ? item.tipEnabled : m.tipEnabled,
            pricingType: item.pricingType !== undefined ? item.pricingType : m.pricingType,
            minPrice: item.minPrice !== undefined ? item.minPrice : m.minPrice,
            price: item.price !== undefined ? item.price : m.price,
            currency: item.currency || m.currency
          };
        });
        setDisplayItems(merged);
      })
      .catch(() => setDisplayItems(items));
  }, [items]);

  const hasPwyw = displayItems.some(item => item.pricingType === 'pwyw');
  const hasTips = displayItems.some(item => item.tipEnabled);

  const effectiveItems = displayItems.map(item => {
    if (item.pricingType === 'pwyw') {
      const customVal = pwywPrices[item.productId];
      if (customVal !== undefined && customVal !== '') {
        const parsed = parseFloat(customVal);
        if (!isNaN(parsed)) {
          return { ...item, price: parsed };
        }
      }
      return { ...item, price: item.price || 0 };
    }
    return item;
  });

  const totalPrice = effectiveItems.reduce((sum, item) => sum + (item.price * item.quantity), 0) + tipAmount;
  const currency = displayItems.length > 0 ? (displayItems[0].currency || 'USD') : 'USD';
  const isFree = totalPrice === 0;

  // Validate PWYW prices against minPrice and minimum transaction amount
  const getPwywError = () => {
    for (const item of displayItems) {
      if (item.pricingType === 'pwyw') {
        const customVal = pwywPrices[item.productId];
        const effectivePrice = customVal !== undefined && customVal !== ''
          ? parseFloat(customVal)
          : (item.price || 0);
        if (isNaN(effectivePrice)) {
          return `Please enter a valid price for "${item.productName}"`;
        }
        if (item.minPrice > 0 && effectivePrice < item.minPrice) {
          return `Price for "${item.productName}" must be at least ${formatPrice(item.minPrice, item.currency || 'USD')}`;
        }
        // Enforce minimum transaction amount: free (0) or at least 1.00
        if (effectivePrice > 0 && effectivePrice < 1) {
          return `Price for "${item.productName}" must be free (0) or at least ${formatPrice(1, item.currency || 'USD')} due to transaction fees`;
        }
      }
    }
    return null;
  };

  const pwywError = getPwywError();

  // Minimum tip 1 USD/EUR (same as product minimum) to cover fees
  const getTipError = () => {
    if (!hasTips || tipPreset !== 'other') return null;
    const custom = parseFloat(tipCustomInput);
    if (tipCustomInput !== '' && !isNaN(custom) && custom > 0 && custom < 1) {
      return `Tip must be at least ${formatPrice(1, currency)} to cover fees, or choose No tip`;
    }
    return null;
  };
  const tipError = getTipError();

  const handleBuy = async () => {
    if (!user) {
      onRequireLogin?.();
      return;
    }

    if (pwywError || tipError) {
      setError(tipError || pwywError);
      return;
    }
    if (licenseLines.length > 0 && !licenseAck) {
      setError(t('checkout.licenseAck'));
      return;
    }

    setLoading(true);
    setError('');
    try {
      // Pass referral code from URL if present
      const urlParams = new URLSearchParams(window.location.search);
      const referralCode = urlParams.get('ref') || sessionStorage.getItem('productReferralCode') || undefined;

      const { data } = await api.post('/payments/stripe/checkout/items', {
        items: effectiveItems.map(item => ({
          productId: item.productId,
          price: item.price,
          quantity: item.quantity || 1,
          currency: item.currency || 'USD'
        })),
        successUrl: window.location.origin + '/checkout/success?session_id={CHECKOUT_SESSION_ID}',
        cancelUrl: window.location.origin + '/checkout/cancel?session_id={CHECKOUT_SESSION_ID}',
        referralCode,
        tipAmount: tipAmount > 0 ? tipAmount : undefined,
        pwywPrices: hasPwyw ? pwywPrices : undefined,
      });
      
      // For free orders, don't store pendingOrderNumber (no cleanup needed)
      // For paid orders, store orderNumber for cleanup if checkout is cancelled
      if (data.orderNumber && !data.isFree) {
        sessionStorage.setItem('pendingOrderNumber', data.orderNumber);
      }
      
      // Call onCheckout callback if provided (for cart modal)
      if (onCheckout) {
        onCheckout();
      }
      
      // Redirect to Stripe checkout or success page
      if (data.url) {
        window.location.href = data.url;
      } else {
        window.location.href = `/checkout/success?orderNumber=${data.orderNumber}`;
      }
    } catch (e) {
      console.error('Checkout error:', e);
      const errorMessage = e.response?.data?.message || e.response?.data?.error || e.message || 'Failed to start checkout. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const licenseLines = license ? buildLicenseSummary(license) : [];

  return (
    <BuyButtonContainer>
      {licenseLines.length > 0 && (
        <div style={{ fontSize: '0.8rem', color: '#9ca3af', marginBottom: '0.5rem' }}>
          <strong style={{ color: '#e5e7eb' }}>License at checkout:</strong>
          <ul style={{ margin: '4px 0 0', paddingLeft: '1.1rem' }}>
            {licenseLines.map((line) => <li key={line}>{line}</li>)}
          </ul>
        </div>
      )}
      {/* PWYW price input */}
      {hasPwyw && displayItems.filter(i => i.pricingType === 'pwyw').map(item => (
        <div key={item.productId} style={{ marginBottom: '0.5rem' }}>
          <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#7c5cff', marginBottom: '0.35rem' }}>
            Name your price for "{item.productName}"
            {item.minPrice > 0 ? ` (min ${formatPrice(item.minPrice, item.currency || 'USD')})` : ' (any amount)'}
          </div>
          <PwywInput
            type="number"
            step="0.01"
            min={item.minPrice >= 1 ? item.minPrice : 0}
            value={pwywPrices[item.productId] ?? (item.price > 0 ? item.price : '')}
            onChange={(e) => setPwywPrices(prev => ({ ...prev, [item.productId]: e.target.value }))}
            placeholder={item.minPrice >= 1 ? `${item.minPrice.toFixed(2)}` : '0.00'}
          />
        </div>
      ))}

      {/* Tip selector - show when any product has tips enabled (including free products) */}
      {hasTips && (
        <div style={{ marginBottom: '0.5rem' }}>
          <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#10b981', marginBottom: '0.35rem' }}>
            Add a tip to support the creator
          </div>
          <TipInput>
            {[0, 1, 2, 5].map(amount => (
              <TipButton
                key={amount}
                $active={tipPreset === amount}
                onClick={() => { setTipPreset(amount); setTipCustomInput(''); }}
                type="button"
              >
                {amount === 0 ? 'No tip' : formatPrice(amount, currency)}
              </TipButton>
            ))}
            <TipButton
              $active={tipPreset === 'other'}
              onClick={() => setTipPreset('other')}
              type="button"
            >
              Other
            </TipButton>
          </TipInput>
          {tipPreset === 'other' && (
            <div style={{ marginTop: '0.35rem' }}>
              <label style={{ fontSize: '0.75rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>
                Enter amount ({currency}) — min {formatPrice(1, currency)} to cover fees
              </label>
              <TipCustomInput
                type="number"
                step="0.01"
                min="1"
                placeholder="1.00"
                value={tipCustomInput}
                onChange={(e) => setTipCustomInput(e.target.value)}
              />
            </div>
          )}
        </div>
      )}

      <PriceDisplay>
        {isFree ? 'Free' : formatPrice(totalPrice, currency)}
        {tipAmount > 0 && <span style={{ fontSize: '0.75rem', color: '#10b981', display: 'block' }}>includes {formatPrice(tipAmount, currency)} tip</span>}
      </PriceDisplay>
      {addToCartSlot ? (
        <ButtonRow>
          {addToCartSlot}
          <BuyButton $inline onClick={handleBuy} disabled={loading || !!pwywError || !!tipError}>
            {loading ? (
              <>
                <LoadingSpinner />
                Processing...
              </>
            ) : (
              <>
                {isFree ? '📥 Get Free' : '🛒 Buy Now'}
              </>
            )}
          </BuyButton>
        </ButtonRow>
      ) : (
        <BuyButton onClick={handleBuy} disabled={loading || !!pwywError || !!tipError}>
          {loading ? (
            <>
              <LoadingSpinner />
              Processing...
            </>
          ) : (
            <>
              {isFree ? '📥 Get Free' : '🛒 Buy Now'}
            </>
          )}
        </BuyButton>
      )}
      {(error || pwywError || tipError) && <ErrorMessage>{error || tipError || pwywError}</ErrorMessage>}
    </BuyButtonContainer>
  );
};

export default CheckoutButtons;


